<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import PendingRowResolveDialog from '@/components/shipment/pending-imports/PendingRowResolveDialog.vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'PackingLists',
    pageTitle: 'Pending Imports',
    breadcrumb: [
      { title: 'Home', to: '/' },
      { title: 'Shipment', to: '/shipment' },
      { title: 'Pending Imports', active: true },
    ],
  },
})

const { t } = useI18n()

// --- state ---
const rows         = ref([])
const total        = ref(0)
const page         = ref(1)
const perPage      = ref(25)
const loading      = ref(false)
const badgeCount   = ref(0)

// --- filters ---
const status = ref('pending')
const reason = ref(null)
const search = ref('')

// Debounce search input so we don't hammer the API on every keystroke.
let searchTimer = null
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => { page.value = 1; load() }, 350)
})
watch([status, reason], () => { page.value = 1; load() })
watch(page, () => load())

const statusOptions = computed(() => [
  { value: 'pending',    title: t('shipment.pending_imports.status_pending') },
  { value: 'resolved',   title: t('shipment.pending_imports.status_resolved') },
  { value: 'dismissed',  title: t('shipment.pending_imports.status_dismissed') },
  { value: 'all',        title: t('shipment.pending_imports.status_all') },
])

const reasonOptions = computed(() => [
  { value: null,                    title: t('shipment.pending_imports.reason_any') },
  { value: 'no_barcode_no_match',   title: t('shipment.pending_imports.reason_no_barcode_no_match') },
  { value: 'smart_create_failed',   title: t('shipment.pending_imports.reason_smart_create_failed') },
  { value: 'parse_error',           title: t('shipment.pending_imports.reason_parse_error') },
  { value: 'unmatched_contact',     title: t('shipment.pending_imports.reason_unmatched_contact') },
  { value: 'other',                 title: t('shipment.pending_imports.reason_other') },
])

const reasonChip = r => {
  /* eslint-disable camelcase */
  const map = {
    no_barcode_no_match: { color: 'warning',   label: t('shipment.pending_imports.chip_no_barcode') },
    smart_create_failed: { color: 'error',     label: t('shipment.pending_imports.chip_create_failed') },
    parse_error: { color: 'error',     label: t('shipment.pending_imports.chip_parse_error') },
    unmatched_contact: { color: 'info',      label: t('shipment.pending_imports.chip_unmatched_contact') },
    other: { color: 'secondary', label: t('shipment.pending_imports.chip_other') },
  }
  /* eslint-enable camelcase */

  return map[r] ?? { color: 'secondary', label: r }
}

const statusChip = s => {
  const map = {
    pending: { color: 'warning',   label: t('shipment.pending_imports.status_pending') },
    resolved: { color: 'success',   label: t('shipment.pending_imports.status_resolved') },
    dismissed: { color: 'secondary', label: t('shipment.pending_imports.status_dismissed') },
  }

  
  return map[s] ?? { color: 'secondary', label: s }
}

// --- data fetch ---
async function load() {
  loading.value = true
  try {
    const { data } = await axios.get('shipment/pending-pl-rows', {
      params: {
        status: status.value,
        reason: reason.value || undefined,
        q: search.value || undefined,
        page: page.value,
        // eslint-disable-next-line camelcase
        per_page: perPage.value,
      },
    })

    rows.value  = data.data ?? []
    total.value = data.total ?? 0
  } finally {
    loading.value = false
  }
}

async function loadBadge() {
  try {
    const { data } = await axios.get('shipment/pending-pl-rows/badge')

    badgeCount.value = data.pending ?? 0
  } catch { /* non-fatal */ }
}

onMounted(() => { load(); loadBadge() })

// --- resolve dialog ---
const dialogOpen   = ref(false)
const dialogRow    = ref(null)

function openResolve(row) {
  dialogRow.value  = row
  dialogOpen.value = true
}

function onResolved() {
  // Row state changed — refresh both the list and the badge.
  load()
  loadBadge()
}

// --- ignore / dismiss ---
//
// A row is "truly empty" when the import couldn't recover anything
// useful: no barcode, no bag code, no customer info, no product name.
// Those are PL formatting noise that the operator should batch-dismiss.
function isEmptyRow(row) {
  const r = row?.raw_row || {}

  return !r.warehouse_receipt
    && !r.bag_code
    && !r.customer_name
    && !r.customer_phone
    && !r.product_name
}

const dismissing = ref(null) // row id currently being dismissed
async function ignoreRow(row) {
  if (dismissing.value) return
  dismissing.value = row.id
  try {
    await axios.post(`shipment/pending-pl-rows/${row.id}/dismiss`, {
      note: isEmptyRow(row)
        ? t('shipment.pending_imports.empty_row_note')
        : t('shipment.pending_imports.ignored_note'),
    })
    load()
    loadBadge()
  } catch (err) {
    console.error('dismiss failed', err)
  } finally {
    dismissing.value = null
  }
}

const bulkIgnoring = ref(false)
async function ignoreAllEmpty() {
  if (bulkIgnoring.value) return
  const empties = rows.value.filter(r => r.status === 'pending' && isEmptyRow(r))
  if (!empties.length) return
  if (!window.confirm(t('shipment.pending_imports.bulk_ignore_confirm', { count: empties.length }))) {
    return
  }
  bulkIgnoring.value = true
  try {
    // Sequential to avoid hammering the API; counts are small (page-bound).
    for (const r of empties) {
      // eslint-disable-next-line no-await-in-loop
      await axios.post(`shipment/pending-pl-rows/${r.id}/dismiss`, {
        note: t('shipment.pending_imports.empty_row_note'),
      })
    }
    load()
    loadBadge()
  } catch (err) {
    console.error('bulk ignore failed', err)
  } finally {
    bulkIgnoring.value = false
  }
}

const emptyRowCount = computed(
  () => rows.value.filter(r => r.status === 'pending' && isEmptyRow(r)).length,
)

const totalPages = computed(() => Math.max(1, Math.ceil(total.value / perPage.value)))
</script>

<template>
  <section>
    <div class="d-flex align-center justify-space-between mb-4 flex-wrap gap-2">
      <div>
        <h4 class="text-h4">
          {{ t('shipment.pending_imports.title') }}
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          {{ t('shipment.pending_imports.subtitle') }}
        </p>
      </div>
      <div class="d-flex align-center gap-2 flex-wrap">
        <VBtn
          v-if="emptyRowCount > 0"
          size="small"
          color="secondary"
          variant="tonal"
          prepend-icon="tabler-eye-off"
          :loading="bulkIgnoring"
          @click="ignoreAllEmpty"
        >
          {{ t('shipment.pending_imports.ignore_all_empty', { count: emptyRowCount }) }}
        </VBtn>
        <VChip
          :color="badgeCount > 0 ? 'warning' : 'success'"
          size="large"
          variant="tonal"
          prepend-icon="tabler-inbox"
        >
          {{ t('shipment.pending_imports.pending_badge', { count: badgeCount }) }}
        </VChip>
      </div>
    </div>

    <VCard>
      <VCardText>
        <div class="d-flex flex-wrap align-center gap-3 mb-4">
          <VSelect
            v-model="status"
            :items="statusOptions"
            :label="t('shipment.pending_imports.filter_status')"
            density="compact"
            style="max-inline-size: 180px;"
          />
          <VSelect
            v-model="reason"
            :items="reasonOptions"
            :label="t('shipment.pending_imports.filter_reason')"
            density="compact"
            style="max-inline-size: 240px;"
          />
          <VTextField
            v-model="search"
            :label="t('shipment.pending_imports.search_placeholder')"
            density="compact"
            prepend-inner-icon="tabler-search"
            clearable
            style="max-inline-size: 380px;"
          />
          <VSpacer />
          <VBtn
            variant="text"
            prepend-icon="tabler-refresh"
            :loading="loading"
            @click="load"
          >
            {{ t('shipment.pending_imports.refresh') }}
          </VBtn>
        </div>

        <VTable hover>
          <thead>
            <tr>
              <th>#</th>
              <th>{{ t('shipment.pending_imports.col_pl') }}</th>
              <th>{{ t('shipment.pending_imports.col_row') }}</th>
              <th>{{ t('shipment.pending_imports.col_reason') }}</th>
              <th>{{ t('shipment.pending_imports.col_customer') }}</th>
              <th>{{ t('shipment.pending_imports.col_barcode') }}</th>
              <th>{{ t('shipment.pending_imports.col_bag_product') }}</th>
              <th>{{ t('shipment.pending_imports.col_status') }}</th>
              <th class="text-end">
                {{ t('shipment.pending_imports.col_actions') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td
                colspan="9"
                class="text-center py-6"
              >
                <VProgressCircular indeterminate />
              </td>
            </tr>
            <tr v-else-if="!rows.length">
              <td
                colspan="9"
                class="text-center py-8 text-medium-emphasis"
              >
                <VIcon
                  icon="tabler-mood-smile"
                  size="48"
                  class="mb-2"
                />
                <div>{{ t('shipment.pending_imports.inbox_empty') }}</div>
              </td>
            </tr>
            <tr
              v-for="row in rows"
              :key="row.id"
            >
              <td>{{ row.id }}</td>
              <td>
                <RouterLink
                  v-if="row.packing_list"
                  :to="{ name: 'shipment-packing-lists-id-view', params: { id: row.packing_list_id } }"
                  class="text-primary"
                >
                  {{ row.packing_list.reference_code || row.packing_list.reference || `#${row.packing_list_id}` }}
                </RouterLink>
                <span v-else>—</span>
              </td>
              <td>{{ row.row_index }}</td>
              <td>
                <VChip
                  :color="reasonChip(row.reason).color"
                  variant="tonal"
                  size="small"
                >
                  {{ reasonChip(row.reason).label }}
                </VChip>
              </td>
              <td>
                <div class="text-body-2">
                  {{ row.raw_row?.customer_name || '—' }}
                </div>
                <div
                  v-if="row.raw_row?.customer_phone"
                  class="text-caption text-medium-emphasis"
                >
                  {{ row.raw_row.customer_phone }}
                </div>
              </td>
              <td>
                <code
                  v-if="row.raw_row?.warehouse_receipt"
                  class="text-caption"
                >
                  {{ row.raw_row.warehouse_receipt }}
                </code>
                <span
                  v-else
                  class="text-medium-emphasis"
                >—</span>
              </td>
              <td>
                <div class="text-caption">
                  {{ row.raw_row?.bag_code || '—' }}
                </div>
                <div
                  class="text-caption text-medium-emphasis text-truncate"
                  style="max-inline-size: 200px;"
                >
                  {{ row.raw_row?.product_name || '' }}
                </div>
              </td>
              <td>
                <VChip
                  :color="statusChip(row.status).color"
                  variant="tonal"
                  size="small"
                >
                  {{ statusChip(row.status).label }}
                </VChip>
              </td>
              <td class="text-end">
                <template v-if="row.status === 'pending'">
                  <div class="d-flex gap-1 justify-end flex-wrap">
                    <VBtn
                      size="small"
                      color="primary"
                      variant="tonal"
                      prepend-icon="tabler-check"
                      @click="openResolve(row)"
                    >
                      {{ t('shipment.pending_imports.resolve') }}
                    </VBtn>
                    <VBtn
                      size="small"
                      color="warning"
                      variant="outlined"
                      prepend-icon="tabler-eye-off"
                      :loading="dismissing === row.id"
                      :title="isEmptyRow(row) ? t('shipment.pending_imports.ignore_empty_hint') : t('shipment.pending_imports.ignore_hint')"
                      @click="ignoreRow(row)"
                    >
                      {{ t('shipment.pending_imports.ignore') }}
                    </VBtn>
                  </div>
                </template>
                <span
                  v-else
                  class="text-caption text-medium-emphasis"
                >
                  {{ row.resolution_note || row.status }}
                </span>
              </td>
            </tr>
          </tbody>
        </VTable>

        <div class="d-flex align-center justify-space-between mt-4">
          <div class="text-caption text-medium-emphasis">
            {{ t('shipment.pending_imports.total_count', { count: total }) }}
          </div>
          <VPagination
            v-model="page"
            :length="totalPages"
            :total-visible="5"
            density="compact"
          />
        </div>
      </VCardText>
    </VCard>

    <PendingRowResolveDialog
      v-model="dialogOpen"
      :row="dialogRow"
      @resolved="onResolved"
    />
  </section>
</template>
